// Correcciones de venta (NCV · NDV · devoluciones · reversas) · docs/108 B7. DOCUMENTO ≠ EFECTO
// FÍSICO ≠ EFECTO ECONÓMICO:
//   - la DEVOLUCIÓN del cliente vuelve de un despacho confirmado y, al confirmarse, mete stock;
//   - la NC/ND es documental (DTE 61/56) sobre una factura o boleta del ERP, con su clase (A devolución,
//     B descuento, C precio, D anulación, E texto) y su causa; puede pedir la firma de otra persona;
//   - la REVERSA de un despacho la pide una persona con su plan y la aprueba otra.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useDespachosDeNota, useDocumentacion, useNota, useNotas } from '../api/consultas';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { Modal } from '../comun/Modal';
import { decimal, entradaDecimal, fecha, fechaHora, pesos } from '../comun/formato';
import { cuatroDecimales, DialogoMotivo } from '../compras/comun';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBodega, useBodegas } from '../stock/PantallasStock';

type Fila = Record<string, unknown> & {
  id: string;
  numero: string | number;
  estado: string;
  version: number;
};

const claves = {
  todo: (e: string) => ['emp', e, 'correcciones-venta'] as const,
  lista: (e: string, que: string, estado: string) =>
    ['emp', e, 'correcciones-venta', que, estado] as const,
};

function useAccion(empresaId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<unknown, Error, { ruta: string; cuerpo: unknown }>({
    mutationFn: ({ ruta, cuerpo }) =>
      api.post(ruta, cuerpo, { idempotencia: intentos.clave(`corr:${ruta}`, cuerpo) }),
    onSuccess: async () => {
      intentos.cerrar();
      await Promise.all([
        qc.invalidateQueries({ queryKey: claves.todo(empresaId) }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'notas'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'disponibilidad'] }),
      ]);
    },
    onError: (e) => intentos.cerrar(e),
  });
}

function useLista(empresaId: string, ruta: string, estado: string) {
  return useQuery({
    queryKey: claves.lista(empresaId, ruta, estado),
    queryFn: () =>
      api.get<{ items: Fila[] }>(ruta, { query: { empresaId, estado: estado || undefined } }),
  });
}

const NOMBRE: Record<string, string> = {
  BORRADOR: 'Borrador',
  PENDIENTE_APROBACION: 'Por aprobar',
  APROBADA: 'Aprobada',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  VIGENTE: 'Vigente',
  LIBERADA: 'Liberada',
  RECHAZADA: 'Rechazada',
  SOLICITADA: 'Solicitada',
  EJECUTADA: 'Ejecutada',
  RETIRADA: 'Retirada',
  ANULACION_SOLICITADA: 'Anulación solicitada',
};

const CLASES: Record<string, { nombre: string; causas: string[]; tipos: Array<'NC' | 'ND'> }> = {
  A: { nombre: 'A · Devolución de mercadería', causas: ['DEVOLUCION'], tipos: ['NC'] },
  B: {
    nombre: 'B · Descuento o bonificación',
    causas: ['DESCUENTO', 'BONIFICACION', 'CORRECCION_MANUAL'],
    tipos: ['NC'],
  },
  C: {
    nombre: 'C · Corrección de precio',
    causas: ['PRECIO', 'CORRECCION_MANUAL'],
    tipos: ['NC', 'ND'],
  },
  D: { nombre: 'D · Anulación del documento', causas: ['ANULACION'], tipos: ['NC'] },
  E: { nombre: 'E · Corrección de texto', causas: ['CORRECCION_TEXTO'], tipos: ['NC'] },
};

/** Elegir una nota de venta por su número (el origen de toda corrección). */
function ElegirNota({ alElegir }: { alElegir: (notaId: string) => void }) {
  const { empresa } = useSesion();
  const [numero, setNumero] = useState('');
  const q = useNotas(
    empresa!.id,
    { numero: /^\d+$/.test(numero) ? numero : undefined, pagina: 1 },
    /^\d+$/.test(numero),
  );
  return (
    <div className="barra-filtros">
      <label className="campo">
        <span className="etiqueta">N° de la nota de venta</span>
        <input
          aria-label="Número de nota"
          autoFocus
          inputMode="numeric"
          value={numero}
          onChange={(e) => setNumero(e.target.value)}
        />
      </label>
      {(q.data?.items ?? []).map((n) => (
        <button key={n.id} type="button" className="primario" onClick={() => alElegir(n.id)}>
          {n.numeroVisible} · {n.cliente_razon_social}
        </button>
      ))}
      {q.data && !q.data.items.length && (
        <span className="apagado">No hay una nota con ese número.</span>
      )}
    </div>
  );
}

// ================================================================ vista

type Seccion = 'nc' | 'devoluciones' | 'reversas';

export function CorreccionesVenta({ seccionInicial }: { seccionInicial?: string | undefined }) {
  const [seccion, setSeccion] = useState<Seccion>((seccionInicial as Seccion) ?? 'nc');
  return (
    <div className="pantalla consulta" data-pantalla="correcciones-venta">
      <div className="barra-filtros" role="tablist" aria-label="Correcciones de venta">
        {(
          [
            ['nc', 'Notas de crédito / débito'],
            ['devoluciones', 'Devoluciones de clientes'],
            ['reversas', 'Reversas de despacho'],
          ] as const
        ).map(([k, t]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={seccion === k}
            className={seccion === k ? 'primario' : undefined}
            onClick={() => setSeccion(k)}
          >
            {t}
          </button>
        ))}
      </div>
      {seccion === 'nc' && <NotasAjuste />}
      {seccion === 'devoluciones' && <DevolucionesCliente />}
      {seccion === 'reversas' && <ReversasDespacho />}
    </div>
  );
}

function FiltroEstado({
  estados,
  valor,
  alCambiar,
}: {
  estados: string[];
  valor: string;
  alCambiar: (v: string) => void;
}) {
  return (
    <label className="campo">
      <span className="etiqueta">Estado</span>
      <select aria-label="Estado" value={valor} onChange={(e) => alCambiar(e.target.value)}>
        <option value="">Todos</option>
        {estados.map((e) => (
          <option key={e} value={e}>
            {NOMBRE[e] ?? e}
          </option>
        ))}
      </select>
    </label>
  );
}

type Resolver = {
  ruta: string;
  titulo: string;
  boton: string;
  cuerpo: (texto: string) => unknown;
  etiqueta?: string;
} | null;

function Acciones({
  botones,
  alResolver,
}: {
  botones: Array<[string, Resolver | (() => void)]>;
  alResolver: (r: Resolver) => void;
}) {
  return (
    <span className="acciones-fila">
      {botones.map(([t, r]) => (
        <button
          key={t}
          type="button"
          className="boton-mini"
          onClick={() => (typeof r === 'function' ? r() : alResolver(r))}
        >
          {t}
        </button>
      ))}
    </span>
  );
}

// ================================================================ NC / ND

function NotasAjuste() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [estado, setEstado] = useState('');
  const q = useLista(empresaId, '/ventas/notas-ajuste', estado);
  const accion = useAccion(empresaId);
  const [resolver, setResolver] = useState<Resolver>(null);
  const [nueva, setNueva] = useState(false);
  const columnas: Columna<Fila>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (a) => <span className="mono">{`${a['tipo']}-${a.numero}`}</span>,
      ancho: '7rem',
    },
    { id: 'f', titulo: 'Creada', celda: (a) => fechaHora(String(a['creado_en'])), ancho: '10rem' },
    {
      id: 'c',
      titulo: 'Clase',
      celda: (a) => CLASES[String(a['clase'])]?.nombre ?? String(a['clase']),
    },
    { id: 'd', titulo: 'Descripción', celda: (a) => String(a['descripcion'] ?? '') },
    {
      id: 'b',
      titulo: 'Bruto',
      celda: (a) => pesos(a['bruto'] as string | null),
      alinear: 'der',
      ancho: '8rem',
    },
    { id: 'e', titulo: 'Estado', celda: (a) => NOMBRE[a.estado] ?? a.estado, ancho: '9rem' },
    {
      id: 'acc',
      titulo: '',
      ancho: '15rem',
      celda: (a) => {
        const ruta = `/ventas/notas-ajuste/${a.id}`;
        const nc = a['tipo'] === 'NC';
        const b: Array<[string, Resolver]> = [];
        if (
          a.estado === 'PENDIENTE_APROBACION' &&
          puede(nc ? 'ventas.nc.aprobar' : 'ventas.nd.aprobar')
        )
          b.push(
            [
              'Aprobar',
              {
                ruta: `${ruta}/aprobar`,
                titulo: 'Aprobar (firma de otra persona)',
                boton: 'Aprobar y emitir',
                etiqueta: 'Comentario',
                cuerpo: (t) => ({ comentario: t }),
              },
            ],
            [
              'Rechazar',
              {
                ruta: `${ruta}/rechazar`,
                titulo: 'Rechazar NC/ND',
                boton: 'Rechazar',
                cuerpo: (t) => ({ motivo: t }),
              },
            ],
          );
        if (a.estado === 'PENDIENTE_APROBACION')
          b.push([
            'Cancelar',
            {
              ruta: `${ruta}/cancelar`,
              titulo: 'Cancelar NC/ND',
              boton: 'Cancelar',
              cuerpo: (t) => ({ motivo: t }),
            },
          ]);
        if (a.estado === 'VIGENTE' && puede('dte.anular'))
          b.push([
            'Liberar',
            {
              ruta: `${ruta}/liberar`,
              titulo: 'Anular antes del envío y liberar',
              boton: 'Liberar',
              cuerpo: (t) => ({ motivo: t }),
            },
          ]);
        return <Acciones botones={b} alResolver={setResolver} />;
      },
    },
  ];
  return (
    <>
      <div className="barra-filtros">
        <FiltroEstado
          estados={['PENDIENTE_APROBACION', 'VIGENTE', 'LIBERADA', 'RECHAZADA', 'CANCELADA']}
          valor={estado}
          alCambiar={setEstado}
        />
        <span className="espaciador" />
        {(puede('ventas.nc.emitir') || puede('ventas.nd.emitir')) && (
          <button type="button" className="primario" onClick={() => setNueva(true)}>
            Nueva NC / ND
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Notas de crédito y débito de venta"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(a) => a.id}
        cargando={q.isLoading}
        vacio="Sin NC / ND"
      />
      {accion.isError && !resolver && <PanelError error={accion.error} />}
      <DialogoResolver r={resolver} accion={accion} alCerrar={() => setResolver(null)} />
      {nueva && <NuevaNotaAjuste alCerrar={() => setNueva(false)} />}
    </>
  );
}

function DialogoResolver({
  r,
  accion,
  alCerrar,
}: {
  r: Resolver;
  accion: ReturnType<typeof useAccion>;
  alCerrar: () => void;
}) {
  if (!r) return null;
  return (
    <DialogoMotivo
      titulo={r.titulo}
      textoBoton={r.boton}
      {...(r.etiqueta ? { etiqueta: r.etiqueta } : {})}
      pendiente={accion.isPending}
      error={accion.isError ? accion.error : null}
      alConfirmar={(t) =>
        accion.mutate({ ruta: r.ruta, cuerpo: r.cuerpo(t) }, { onSuccess: alCerrar })
      }
      alCerrar={alCerrar}
    />
  );
}

function NuevaNotaAjuste({ alCerrar }: { alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccion(empresaId);
  const [notaId, setNotaId] = useState<string | null>(null);
  const nota = useNota(empresaId, notaId);
  const doc = useDocumentacion(empresaId, notaId);
  const [documentoId, setDocumentoId] = useState('');
  const [tipo, setTipo] = useState<'NC' | 'ND'>('NC');
  const [clase, setClase] = useState('B');
  const [causa, setCausa] = useState('DESCUENTO');
  const [descripcion, setDescripcion] = useState('');
  const [razon, setRazon] = useState('');
  const [texto, setTexto] = useState('');
  const [devolucionId, setDevolucionId] = useState('');
  const [montos, setMontos] = useState<Record<number, string>>({});
  const [evidencia, setEvidencia] = useState('');
  const devoluciones = useQuery({
    queryKey: ['emp', empresaId, 'correcciones-venta', 'dev-nota', notaId],
    queryFn: () =>
      api.get<{ items: Fila[] }>('/ventas/devoluciones', {
        query: { empresaId, notaId: notaId!, estado: 'CONFIRMADA' },
      }),
    enabled: !!notaId && clase === 'A',
  });
  const saldo = useQuery({
    queryKey: ['emp', empresaId, 'correcciones-venta', 'saldo', documentoId],
    queryFn: () =>
      api.get<{
        lineas: Array<{
          lineaNumero: number;
          documentado: string;
          acreditado: string;
          debitado: string;
          disponible: string;
        }>;
      }>(`/dte/documentos/${documentoId}/saldo-ajustes`),
    enabled: !!documentoId && (clase === 'B' || clase === 'C'),
  });
  const documentos = (doc.data?.documentos ?? []).filter((d) => d.estado === 'VIGENTE' && d.dte);
  const lineaNota = (n: number) => nota.data?.lineas.find((l) => l.numero === n);
  const imputaciones = Object.entries(montos)
    .filter(([, v]) => v.trim())
    .map(([n, v]) => ({ lineaNumero: Number(n), bruto: entradaDecimal(v, 0) }));
  const valido =
    !!documentoId &&
    descripcion.trim().length >= 3 &&
    razon.trim().length >= 3 &&
    (clase !== 'A' || !!devolucionId) &&
    ((clase !== 'B' && clase !== 'C') ||
      (imputaciones.length > 0 && imputaciones.every((i) => i.bruto))) &&
    (clase !== 'E' || texto.trim().length >= 3);
  const emitir = () =>
    accion.mutate(
      {
        ruta: '/ventas/notas-ajuste',
        cuerpo: {
          documentoOriginalId: documentoId,
          tipo,
          clase,
          causa,
          descripcion: descripcion.trim(),
          razon: razon.trim().slice(0, 90),
          ...(clase === 'A' ? { devolucionId } : {}),
          ...(clase === 'B' || clase === 'C' ? { imputaciones } : {}),
          ...(clase === 'E' ? { textoCorreccion: texto.trim().slice(0, 80) } : {}),
          ...(evidencia.trim()
            ? { evidencia: [{ tipo: 'DOCUMENTO', referencia: evidencia.trim() }] }
            : {}),
        },
      },
      { onSuccess: alCerrar },
    );
  return (
    <Modal titulo="Nueva nota de crédito / débito de venta" alCerrar={alCerrar} ancho="ancho">
      {!notaId ? (
        <ElegirNota alElegir={setNotaId} />
      ) : (
        <>
          <p>
            Nota <b>{nota.data?.numeroVisible}</b> · {nota.data?.cabecera.clienteRazonSocial}
          </p>
          <div className="barra-filtros">
            <label className="campo">
              <span className="etiqueta">Documento que se corrige</span>
              <select
                aria-label="Documento que se corrige"
                value={documentoId}
                onChange={(e) => setDocumentoId(e.target.value)}
              >
                <option value="">Elegir…</option>
                {documentos.map((d) => (
                  <option key={d.documentacionId} value={d.dte!.documentoId}>
                    {d.tipoDocumento.toLowerCase()} · folio {d.dte!.folio ?? '—'} (
                    {d.dte!.estado.toLowerCase()})
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">Tipo</span>
              <select
                aria-label="Tipo de nota"
                value={tipo}
                onChange={(e) => {
                  const t = e.target.value as 'NC' | 'ND';
                  setTipo(t);
                  if (t === 'ND') {
                    setClase('C');
                    setCausa('PRECIO');
                  }
                }}
              >
                <option value="NC">Nota de crédito</option>
                <option value="ND">Nota de débito</option>
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">Clase</span>
              <select
                aria-label="Clase"
                value={clase}
                onChange={(e) => {
                  setClase(e.target.value);
                  setCausa(CLASES[e.target.value]!.causas[0]!);
                }}
              >
                {Object.entries(CLASES)
                  .filter(([, c]) => c.tipos.includes(tipo))
                  .map(([k, c]) => (
                    <option key={k} value={k}>
                      {c.nombre}
                    </option>
                  ))}
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">Causa</span>
              <select aria-label="Causa" value={causa} onChange={(e) => setCausa(e.target.value)}>
                {CLASES[clase]!.causas.map((c) => (
                  <option key={c} value={c}>
                    {c.toLowerCase().replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {documentos.length === 0 && doc.data && (
            <p className="tono-malo">Esta nota no tiene factura ni boleta vigente que corregir.</p>
          )}
          {clase === 'A' && (
            <label className="campo">
              <span className="etiqueta">Devolución confirmada que acredita</span>
              <select
                aria-label="Devolución"
                value={devolucionId}
                onChange={(e) => setDevolucionId(e.target.value)}
              >
                <option value="">Elegir…</option>
                {(devoluciones.data?.items ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    Devolución {d.numero} · {fecha(String(d['fecha_negocio']))}
                  </option>
                ))}
              </select>
            </label>
          )}
          {(clase === 'B' || clase === 'C') && documentoId && (
            <table className="grilla compacta" aria-label="Montos por línea">
              <thead>
                <tr>
                  <th>Línea</th>
                  <th>Producto</th>
                  <th className="der">Documentado</th>
                  <th className="der">Disponible</th>
                  <th className="der">Monto bruto</th>
                </tr>
              </thead>
              <tbody>
                {(saldo.data?.lineas ?? []).map((l) => (
                  <tr key={l.lineaNumero}>
                    <td>{l.lineaNumero}</td>
                    <td>{lineaNota(l.lineaNumero)?.descripcion ?? ''}</td>
                    <td className="der">{decimal(l.documentado)}</td>
                    <td className="der">{decimal(l.disponible)}</td>
                    <td className="der">
                      <input
                        className="entrada-numero"
                        aria-label={`Monto línea ${l.lineaNumero}`}
                        value={montos[l.lineaNumero] ?? ''}
                        onChange={(e) =>
                          setMontos((m) => ({ ...m, [l.lineaNumero]: e.target.value }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {clase === 'E' && (
            <label className="campo">
              <span className="etiqueta">Corrección («donde dice… debe decir…», hasta 80)</span>
              <input
                aria-label="Texto de la corrección"
                maxLength={80}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />
            </label>
          )}
          <label className="campo">
            <span className="etiqueta">Qué pasó</span>
            <input
              aria-label="Descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Razón de la referencia (va en el DTE, hasta 90)</span>
            <input
              aria-label="Razón"
              maxLength={90}
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Evidencia (opcional: n° de documento, correo, foto)</span>
            <input
              aria-label="Evidencia"
              value={evidencia}
              onChange={(e) => setEvidencia(e.target.value)}
            />
          </label>
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={alCerrar}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={!valido || accion.isPending}
              onClick={emitir}
            >
              Solicitar {tipo}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ================================================================ devoluciones del cliente

function DevolucionesCliente() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [estado, setEstado] = useState('');
  const q = useLista(empresaId, '/ventas/devoluciones', estado);
  const accion = useAccion(empresaId);
  const [resolver, setResolver] = useState<Resolver>(null);
  const [nueva, setNueva] = useState(false);
  const simple = (ruta: string, version: number) => () =>
    accion.mutate({ ruta, cuerpo: { version } });
  const columnas: Columna<Fila>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (d) => <span className="mono">{`DEV-${d.numero}`}</span>,
      ancho: '7rem',
    },
    { id: 'f', titulo: 'Fecha', celda: (d) => fecha(String(d['fecha_negocio'])), ancho: '7rem' },
    { id: 'm', titulo: 'Motivo', celda: (d) => String(d['motivo'] ?? '') },
    {
      id: 'r',
      titulo: 'Firma',
      celda: (d) => (d['requiere_firma'] ? 'requiere' : ''),
      ancho: '6rem',
    },
    { id: 'e', titulo: 'Estado', celda: (d) => NOMBRE[d.estado] ?? d.estado, ancho: '8rem' },
    {
      id: 'acc',
      titulo: '',
      ancho: '17rem',
      celda: (d) => {
        const ruta = `/ventas/devoluciones/${d.id}`;
        const v = d.version;
        const b: Array<[string, Resolver | (() => void)]> = [];
        if (d.estado === 'BORRADOR' && d['requiere_firma'])
          b.push(['Pedir aprobación', simple(`${ruta}/solicitar-aprobacion`, v)]);
        if ((d.estado === 'BORRADOR' && !d['requiere_firma']) || d.estado === 'APROBADA')
          if (puede('ventas.devolucion.confirmar'))
            b.push(['Confirmar (entra stock)', simple(`${ruta}/confirmar`, v)]);
        if (d.estado === 'PENDIENTE_APROBACION' && puede('ventas.devolucion.aprobar'))
          b.push(
            [
              'Aprobar',
              {
                ruta: `${ruta}/aprobar`,
                titulo: 'Aprobar devolución',
                boton: 'Aprobar',
                cuerpo: (t) => ({ version: v, motivo: t }),
              },
            ],
            [
              'Rechazar',
              {
                ruta: `${ruta}/rechazar`,
                titulo: 'Rechazar devolución',
                boton: 'Rechazar',
                cuerpo: (t) => ({ version: v, motivo: t }),
              },
            ],
          );
        if (['BORRADOR', 'PENDIENTE_APROBACION', 'APROBADA'].includes(d.estado))
          b.push([
            'Cancelar',
            {
              ruta: `${ruta}/cancelar`,
              titulo: 'Cancelar devolución',
              boton: 'Cancelar',
              cuerpo: (t) => ({ version: v, motivo: t }),
            },
          ]);
        return <Acciones botones={b} alResolver={setResolver} />;
      },
    },
  ];
  return (
    <>
      <div className="barra-filtros">
        <FiltroEstado
          estados={['BORRADOR', 'PENDIENTE_APROBACION', 'APROBADA', 'CONFIRMADA', 'CANCELADA']}
          valor={estado}
          alCambiar={setEstado}
        />
        <span className="espaciador" />
        {puede('ventas.devolucion.crear') && (
          <button type="button" className="primario" onClick={() => setNueva(true)}>
            Nueva devolución
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Devoluciones de clientes"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(d) => d.id}
        cargando={q.isLoading}
        vacio="Sin devoluciones"
      />
      {accion.isError && !resolver && <PanelError error={accion.error} />}
      <DialogoResolver r={resolver} accion={accion} alCerrar={() => setResolver(null)} />
      {nueva && <NuevaDevolucionCliente alCerrar={() => setNueva(false)} />}
    </>
  );
}

function NuevaDevolucionCliente({ alCerrar }: { alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccion(empresaId);
  const [notaId, setNotaId] = useState<string | null>(null);
  const nota = useNota(empresaId, notaId);
  const despachos = useDespachosDeNota(empresaId, notaId);
  const [despachoId, setDespachoId] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [motivo, setMotivo] = useState('');
  const [cant, setCant] = useState<Record<string, string>>({});
  const devolvible = useQuery({
    queryKey: ['emp', empresaId, 'correcciones-venta', 'devolvible', despachoId],
    queryFn: () =>
      api.get<{
        lineas: Array<{
          despachoLineaId: string;
          numero: number;
          productoId: string;
          despachado: string;
          devuelto: string;
          devolvible: string;
        }>;
      }>(`/ventas/despachos/${despachoId}/devolvible`),
    enabled: !!despachoId,
  });
  const confirmados = (despachos.data?.items ?? []).filter((d) => d.estado === 'CONFIRMADO');
  const nombre = (productoId: string) =>
    nota.data?.lineas.find((l) => l.productoId === productoId)?.descripcion ??
    productoId.slice(0, 8);
  const lineas = Object.entries(cant)
    .filter(([, v]) => v.trim())
    .map(([id, v]) => ({ despachoLineaId: id, cantidadBase: cuatroDecimales(v) }));
  const valido = !!despachoId && motivo.trim().length >= 3 && lineas.length > 0;
  return (
    <Modal titulo="Nueva devolución de cliente" alCerrar={alCerrar} ancho="ancho">
      {!notaId ? (
        <ElegirNota alElegir={setNotaId} />
      ) : (
        <>
          <p>
            Nota <b>{nota.data?.numeroVisible}</b> · {nota.data?.cabecera.clienteRazonSocial}
          </p>
          <div className="barra-filtros">
            <label className="campo">
              <span className="etiqueta">Despacho de donde vuelve</span>
              <select
                aria-label="Despacho"
                value={despachoId}
                onChange={(e) => setDespachoId(e.target.value)}
              >
                <option value="">Elegir…</option>
                {confirmados.map((d) => (
                  <option key={d.id} value={d.id}>
                    Despacho {d.numero} · {fecha(d.fecha_negocio)}
                  </option>
                ))}
              </select>
            </label>
            <SelectorBodega
              bodegas={bodegas.lista}
              valor={ubicacion}
              alCambiar={setUbicacion}
              conTodas
            />
            <span className="apagado">(«Todas» = vuelve a la bodega del despacho)</span>
          </div>
          {despachoId && (
            <table className="grilla compacta" aria-label="Lo que se puede devolver">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th className="der">Despachado</th>
                  <th className="der">Ya devuelto</th>
                  <th className="der">Devolvible</th>
                  <th className="der">Devuelve (unid. base)</th>
                </tr>
              </thead>
              <tbody>
                {(devolvible.data?.lineas ?? []).map((l) => (
                  <tr key={l.despachoLineaId}>
                    <td>{nombre(l.productoId)}</td>
                    <td className="der">{decimal(l.despachado)}</td>
                    <td className="der">{decimal(l.devuelto)}</td>
                    <td className="der">{decimal(l.devolvible)}</td>
                    <td className="der">
                      <input
                        className="entrada-numero"
                        aria-label={`Devuelve línea ${l.numero}`}
                        disabled={!Number(l.devolvible)}
                        value={cant[l.despachoLineaId] ?? ''}
                        onChange={(e) =>
                          setCant((c) => ({ ...c, [l.despachoLineaId]: e.target.value }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo de la devolución"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={alCerrar}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={!valido || accion.isPending}
              onClick={() =>
                accion.mutate(
                  {
                    ruta: '/ventas/devoluciones',
                    cuerpo: {
                      despachoId,
                      motivo: motivo.trim(),
                      lineas,
                      ...(ubicacion ? { ubicacionId: ubicacion } : {}),
                    },
                  },
                  { onSuccess: alCerrar },
                )
              }
            >
              Crear devolución
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ================================================================ reversas de despacho

interface PlanReversa {
  clases: string[];
  pasos: Array<{ orden: number; paso: string; cantidadBase?: string }>;
  bloqueos: Array<{ codigo: string; detalle?: unknown }>;
  ejecutable: boolean;
  hash: string;
}

function ReversasDespacho() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [estado, setEstado] = useState('');
  const q = useLista(empresaId, '/ventas/reversas', estado);
  const accion = useAccion(empresaId);
  const [resolver, setResolver] = useState<Resolver>(null);
  const [nueva, setNueva] = useState(false);
  const columnas: Columna<Fila>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (r) => <span className="mono">{`REV-${r.numero}`}</span>,
      ancho: '7rem',
    },
    {
      id: 'f',
      titulo: 'Solicitada',
      celda: (r) => fechaHora(String(r['solicitada_en'])),
      ancho: '10rem',
    },
    { id: 'm', titulo: 'Motivo', celda: (r) => String(r['motivo'] ?? '') },
    { id: 'e', titulo: 'Estado', celda: (r) => NOMBRE[r.estado] ?? r.estado, ancho: '8rem' },
    {
      id: 'acc',
      titulo: '',
      ancho: '15rem',
      celda: (r) => {
        if (r.estado !== 'SOLICITADA') return null;
        const ruta = `/ventas/reversas/${r.id}`;
        const b: Array<[string, Resolver]> = [];
        if (puede('ventas.reversa.aprobar'))
          b.push(
            [
              'Aprobar',
              {
                ruta: `${ruta}/aprobar`,
                titulo: 'Aprobar y ejecutar la reversa',
                boton: 'Aprobar',
                cuerpo: (t) => ({ motivo: t }),
              },
            ],
            [
              'Rechazar',
              {
                ruta: `${ruta}/rechazar`,
                titulo: 'Rechazar reversa',
                boton: 'Rechazar',
                cuerpo: (t) => ({ motivo: t }),
              },
            ],
          );
        b.push([
          'Retirar',
          {
            ruta: `${ruta}/retirar`,
            titulo: 'Retirar la solicitud',
            boton: 'Retirar',
            cuerpo: (t) => ({ motivo: t }),
          },
        ]);
        return <Acciones botones={b} alResolver={setResolver} />;
      },
    },
  ];
  return (
    <>
      <div className="barra-filtros">
        <FiltroEstado
          estados={['SOLICITADA', 'EJECUTADA', 'RECHAZADA', 'RETIRADA']}
          valor={estado}
          alCambiar={setEstado}
        />
        <span className="espaciador" />
        {puede('ventas.reversa.solicitar') && (
          <button type="button" className="primario" onClick={() => setNueva(true)}>
            Solicitar reversa
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Reversas de despacho"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(r) => r.id}
        cargando={q.isLoading}
        vacio="Sin reversas"
      />
      {accion.isError && !resolver && <PanelError error={accion.error} />}
      <DialogoResolver r={resolver} accion={accion} alCerrar={() => setResolver(null)} />
      {nueva && <NuevaReversa alCerrar={() => setNueva(false)} />}
    </>
  );
}

function NuevaReversa({ alCerrar }: { alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccion(empresaId);
  const [notaId, setNotaId] = useState<string | null>(null);
  const nota = useNota(empresaId, notaId);
  const despachos = useDespachosDeNota(empresaId, notaId);
  const [despachoId, setDespachoId] = useState('');
  const [reserva, setReserva] = useState<'RESTAURAR' | 'LIBERAR'>('RESTAURAR');
  const [motivo, setMotivo] = useState('');
  const plan = useQuery({
    queryKey: ['emp', empresaId, 'correcciones-venta', 'plan', despachoId, reserva],
    queryFn: () =>
      api.get<PlanReversa>(`/ventas/despachos/${despachoId}/plan-reversa`, { query: { reserva } }),
    enabled: !!despachoId,
  });
  const confirmados = (despachos.data?.items ?? []).filter((d) => d.estado === 'CONFIRMADO');
  return (
    <Modal titulo="Solicitar reversa de despacho" alCerrar={alCerrar} ancho="ancho">
      {!notaId ? (
        <ElegirNota alElegir={setNotaId} />
      ) : (
        <>
          <p>
            Nota <b>{nota.data?.numeroVisible}</b> · {nota.data?.cabecera.clienteRazonSocial}
          </p>
          <div className="barra-filtros">
            <label className="campo">
              <span className="etiqueta">Despacho</span>
              <select
                aria-label="Despacho"
                value={despachoId}
                onChange={(e) => setDespachoId(e.target.value)}
              >
                <option value="">Elegir…</option>
                {confirmados.map((d) => (
                  <option key={d.id} value={d.id}>
                    Despacho {d.numero} · {fecha(d.fecha_negocio)}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">La reserva</span>
              <select
                aria-label="Reserva"
                value={reserva}
                onChange={(e) => setReserva(e.target.value as 'RESTAURAR' | 'LIBERAR')}
              >
                <option value="RESTAURAR">Vuelve a la nota (restaurar)</option>
                <option value="LIBERAR">Queda disponible (liberar)</option>
              </select>
            </label>
          </div>
          {plan.isError && <PanelError error={plan.error} />}
          {plan.data && (
            <div className="panel-revision">
              <p>
                Plan: {plan.data.clases.join(', ') || 'sin efectos'} · {plan.data.pasos.length}{' '}
                pasos
              </p>
              <ol>
                {plan.data.pasos.map((p) => (
                  <li key={p.orden}>
                    {p.paso.toLowerCase().replace(/_/g, ' ')}
                    {p.cantidadBase ? ` · ${decimal(p.cantidadBase)}` : ''}
                  </li>
                ))}
              </ol>
              {!plan.data.ejecutable && (
                <p className="tono-malo">
                  Bloqueado:{' '}
                  {plan.data.bloqueos
                    .map((b) => b.codigo.toLowerCase().replace(/_/g, ' '))
                    .join(' · ')}
                </p>
              )}
            </div>
          )}
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo de la reversa"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={alCerrar}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={!plan.data?.ejecutable || motivo.trim().length < 3 || accion.isPending}
              onClick={() =>
                accion.mutate(
                  {
                    ruta: `/ventas/despachos/${despachoId}/reversar`,
                    cuerpo: { motivo: motivo.trim(), reserva, planHash: plan.data!.hash },
                  },
                  { onSuccess: alCerrar },
                )
              }
            >
              Solicitar reversa
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
